import React from 'react';
import {
  Box,
  Heading,
  Checkbox,
  Flex,
  Text,
  Spinner,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
} from '@chakra-ui/react';
import xhr from 'xhr';

/**
 * ProgramSelector - Two-level program selector for Schools
 *
 * Level 1: ProgramTypes (e.g., "Carreras Técnicas", "High School")
 * Level 2: Specific programs within each type (Grades or TechnicalCareers)
 *
 * Uses SchoolProgram entity to store which programs are enabled for a school.
 */
export default function ProgramSelector({ schoolId }) {
  const [programTypes, setProgramTypes] = React.useState([]);
  const [schoolPrograms, setSchoolPrograms] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState({});
  const toast = useToast();

  React.useEffect(() => {
    fetchData();
  }, [schoolId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch all program types and their programs
      const [ptRes, gradesRes, careersRes, coursesRes, spRes] = await Promise.all([
        xhr(`/program-types?populate=*&sort[0]=order:asc&pagination[pageSize]=100`),
        xhr(`/grades?populate=program_type&filters[active][$eq]=true&sort[0]=name:asc&pagination[pageSize]=100`),
        xhr(`/technical-careers?populate=program_type&filters[active][$eq]=true&sort[0]=name:asc&pagination[pageSize]=100`),
        xhr(`/courses?populate=program_type&filters[active][$eq]=true&sort[0]=name:asc&pagination[pageSize]=100`),
        xhr(`/school-programs?filters[school][id][$eq]=${schoolId}&populate=program_type,grade,technical_career,course&pagination[pageSize]=100`),
      ]);

      const types = (ptRes.data.data || []).map((pt) => {
        const attrs = pt.attributes || pt;
        const ptId = pt.id;
        const typeEnum = attrs.type;

        // Get programs for this type based on its enum
        let programs = [];
        if (typeEnum === 'technical_career') {
          programs = (careersRes.data.data || [])
            .filter((c) => {
              const cAttrs = c.attributes || c;
              const cPt = cAttrs.program_type?.data || cAttrs.program_type;
              return cPt?.id === ptId;
            })
            .map((c) => ({
              id: c.id,
              name: (c.attributes || c).name,
              field: 'technical_career',
            }));
        } else if (typeEnum === 'course') {
          programs = (coursesRes.data.data || [])
            .filter((c) => {
              const cAttrs = c.attributes || c;
              const cPt = cAttrs.program_type?.data || cAttrs.program_type;
              return cPt?.id === ptId;
            })
            .map((c) => ({
              id: c.id,
              name: (c.attributes || c).name,
              field: 'course',
            }));
        } else {
          // grade or highschool types
          programs = (gradesRes.data.data || [])
            .filter((g) => {
              const gAttrs = g.attributes || g;
              const gPt = gAttrs.program_type?.data || gAttrs.program_type;
              return gPt?.id === ptId;
            })
            .map((g) => ({
              id: g.id,
              name: (g.attributes || g).name,
              field: 'grade',
            }));
        }

        return {
          id: ptId,
          name: attrs.name,
          type: typeEnum,
          programs,
        };
      });

      setProgramTypes(types);

      // Map existing school programs
      const sp = (spRes.data.data || []).map((item) => {
        const attrs = item.attributes || item;
        const pt = attrs.program_type?.data || attrs.program_type;
        const grade = attrs.grade?.data || attrs.grade;
        const tc = attrs.technical_career?.data || attrs.technical_career;
        const course = attrs.course?.data || attrs.course;
        return {
          id: item.id,
          programTypeId: pt?.id,
          gradeId: grade?.id || null,
          technicalCareerId: tc?.id || null,
          courseId: course?.id || null,
          active: attrs.active !== false,
        };
      });

      setSchoolPrograms(sp);
    } catch (error) {
      console.error('Error fetching program data:', error);
      toast({ title: 'Error al cargar programas', description: 'No se pudieron obtener los programas disponibles', status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  // Check if a specific program is enabled
  const isProgramEnabled = (programTypeId, programId, field) => {
    return schoolPrograms.some(
      (sp) =>
        sp.programTypeId === programTypeId &&
        sp.active &&
        ((field === 'grade' && sp.gradeId === programId) ||
          (field === 'technical_career' && sp.technicalCareerId === programId) ||
          (field === 'course' && sp.courseId === programId))
    );
  };

  // Get the SchoolProgram record for a specific program
  const getSchoolProgram = (programTypeId, programId, field) => {
    return schoolPrograms.find(
      (sp) =>
        sp.programTypeId === programTypeId &&
        ((field === 'grade' && sp.gradeId === programId) ||
          (field === 'technical_career' && sp.technicalCareerId === programId) ||
          (field === 'course' && sp.courseId === programId))
    );
  };

  // Count active programs for a program type
  const getActiveCount = (programTypeId) => {
    return schoolPrograms.filter(
      (sp) => sp.programTypeId === programTypeId && sp.active
    ).length;
  };

  // Toggle a specific program on/off
  const toggleProgram = async (programType, program) => {
    const key = `${programType.id}-${program.id}`;
    setSaving((prev) => ({ ...prev, [key]: true }));

    try {
      const existing = getSchoolProgram(programType.id, program.id, program.field);

      if (existing) {
        // Update existing record
        await xhr.put(`/school-programs/${existing.id}`, {
          data: { active: !existing.active },
        });
        setSchoolPrograms((prev) =>
          prev.map((sp) =>
            sp.id === existing.id ? { ...sp, active: !sp.active } : sp
          )
        );
      } else {
        // Create new record
        const payload = {
          data: {
            school: schoolId,
            program_type: programType.id,
            [program.field]: program.id,
            active: true,
          },
        };
        const res = await xhr.post('/school-programs', payload);
        const newId = res.data.data.id;
        setSchoolPrograms((prev) => [
          ...prev,
          {
            id: newId,
            programTypeId: programType.id,
            gradeId: program.field === 'grade' ? program.id : null,
            technicalCareerId: program.field === 'technical_career' ? program.id : null,
            courseId: program.field === 'course' ? program.id : null,
            active: true,
          },
        ]);
      }
    } catch (error) {
      console.error('Error toggling program:', error);
      toast({ title: 'Error al actualizar programa', description: 'Intenta de nuevo', status: 'error', position: 'bottom', duration: 4000, isClosable: true });
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Toggle all programs for a program type
  const toggleAllPrograms = async (programType, enable) => {
    const key = `type-${programType.id}`;
    setSaving((prev) => ({ ...prev, [key]: true }));

    try {
      for (const program of programType.programs) {
        const existing = getSchoolProgram(programType.id, program.id, program.field);
        const isEnabled = existing?.active || false;

        if (enable && !isEnabled) {
          if (existing) {
            await xhr.put(`/school-programs/${existing.id}`, { data: { active: true } });
          } else {
            await xhr.post('/school-programs', {
              data: {
                school: schoolId,
                program_type: programType.id,
                [program.field]: program.id,
                active: true,
              },
            });
          }
        } else if (!enable && isEnabled && existing) {
          await xhr.put(`/school-programs/${existing.id}`, { data: { active: false } });
        }
      }

      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Error toggling all programs:', error);
      toast({ title: 'Error al actualizar programas', description: 'Intenta de nuevo', status: 'error', position: 'bottom', duration: 4000, isClosable: true });
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="sm" mb={4}>
        Programas Habilitados
      </Heading>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Selecciona los tipos de programa y los programas específicos que esta
        institución puede ofrecer.
      </Text>

      <Accordion allowMultiple>
        {programTypes.map((pt) => {
          const activeCount = getActiveCount(pt.id);
          const allEnabled = activeCount === pt.programs.length && pt.programs.length > 0;

          return (
            <AccordionItem key={pt.id} bg="white" borderRadius="lg" mb={3} boxShadow="sm">
              <AccordionButton py={4} px={6}>
                <Flex flex="1" alignItems="center" gap={3}>
                  <Checkbox
                    colorScheme="purple"
                    isChecked={allEnabled}
                    isIndeterminate={activeCount > 0 && !allEnabled}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleAllPrograms(pt, !allEnabled);
                    }}
                    isDisabled={saving[`type-${pt.id}`]}
                  />
                  <Text fontWeight="semibold">{pt.name}</Text>
                  <Badge colorScheme={activeCount > 0 ? 'purple' : 'gray'} ml={2}>
                    {activeCount} / {pt.programs.length}
                  </Badge>
                  {saving[`type-${pt.id}`] && <Spinner size="sm" ml={2} />}
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4} px={6}>
                {pt.programs.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No hay programas disponibles en este tipo.
                  </Text>
                ) : (
                  <Flex direction="column" gap={2}>
                    {pt.programs.map((program) => {
                      const enabled = isProgramEnabled(pt.id, program.id, program.field);
                      const key = `${pt.id}-${program.id}`;

                      return (
                        <Flex key={program.id} alignItems="center" gap={3} pl={6}>
                          <Checkbox
                            colorScheme="purple"
                            isChecked={enabled}
                            onChange={() => toggleProgram(pt, program)}
                            isDisabled={saving[key]}
                          >
                            {program.name}
                          </Checkbox>
                          {saving[key] && <Spinner size="xs" />}
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
              </AccordionPanel>
            </AccordionItem>
          );
        })}

        {programTypes.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>
            No hay tipos de programa disponibles para Schools.
          </Text>
        )}
      </Accordion>
    </Box>
  );
}
